import type { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import type PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import type PricingRoomWithPriceOutput from '@app/model/pricing-automation/output/PricingRoomWithPriceOutput';
import type PricingIntegrationInput from '@app/model/pricing-automation/input/PricingIntegrationInput';
import type ManualPricingIntegrationInput from '@app/model/pricing-automation/input/ManualPricingIntegrationInput';
import type { PriceEditGroup, PriceEditRoom } from './PriceEditRoomsTable';

// The submit of the Edit Prices popup: a Recommendation Submit (submitPricing) or a manual submit (submitManualPricing).
export type PriceEditMode = 'recommendation' | 'manual';

export type PriceEditPlanInput = {
	statDate: string,
	// The newest states of the open tab (one room type, or every room type on the All tab); empty on a day with no state.
	states: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	// The live WebHotelier prices of the date (findCompanyHotelPricingRoomWithPrice).
	liveRooms: PricingRoomWithPriceOutput[],
	// The states of the date that were sent as a recommendation.
	submittedStateIds: number[],
	// The hotel may send a Recommendation Submit (hasRecommendationCanSubmitPrices).
	canSubmitRecommendation: boolean,
	// The architectures of a room-type tab; undefined lists every room (the All tab, a day with no state).
	architectureIds?: number[],
};

export type PriceEditPlan = {
	mode: PriceEditMode,
	groups: PriceEditGroup[],
	// The rooms left out because WebHotelier has no price for them on the date (an Unpriced Master Room alone stands for
	// its whole room type).
	unpricedRooms: PricingRoomWithPriceOutput[],
	defaultValues: PricingIntegrationInput | ManualPricingIntegrationInput,
};

const round = (price: number) => Math.round(price);

const toDerivativeRule = (rule: { type: string, value?: number | null } | undefined | null): PriceEditRoom['derivativeRule'] =>
	rule === undefined || rule === null ? undefined
		: { type: rule.type, value: rule.value === null || rule.value === undefined ? undefined : Number(rule.value) } as PriceEditRoom['derivativeRule'];

// An Unpriced Master Room leaves out its whole Pricing Architecture and alone names it; an Unpriced Derivative Room
// leaves out only itself.
export const splitUnpricedRooms = (roomsWithPrice: PricingRoomWithPriceOutput[]) => {
	const hiddenArchitectureIds = new Set(roomsWithPrice
		.filter(roomWithPrice => roomWithPrice.isAnchor && roomWithPrice.price === null)
		.map(roomWithPrice => roomWithPrice.architectureId));
	const shownRooms: (PricingRoomWithPriceOutput & { price: number })[] = [];
	const unpricedRooms: PricingRoomWithPriceOutput[] = [];
	roomsWithPrice.forEach(roomWithPrice => {
		if (hiddenArchitectureIds.has(roomWithPrice.architectureId)) {
			if (roomWithPrice.isAnchor) unpricedRooms.push(roomWithPrice);
		} else if (roomWithPrice.price === null) {
			unpricedRooms.push(roomWithPrice);
		} else {
			shownRooms.push({ ...roomWithPrice, price: roomWithPrice.price });
		}
	});
	return { shownRooms, unpricedRooms };
};

// A Recommendation Submit: the states of the tab that were not sent yet (a sent one makes the backend refuse the whole
// submit). New Rate starts at the proposed price of a valid state and at the live price of a held one. currentPrice is
// the night-run base price, as before; the backend reads its own.
const createRecommendationPlan = (input: PriceEditPlanInput, openStates: PriceRecommendationState[]): PriceEditPlan | undefined => {
	const liveByRoomId = new Map(input.liveRooms.map(roomWithPrice => [roomWithPrice.room.id, roomWithPrice]));
	const groups: PriceEditGroup[] = [];
	const rates: PricingIntegrationInput['rates'] = [];
	const unpricedRooms: PricingRoomWithPriceOutput[] = [];

	for (const state of openStates) {
		const architecture = input.architectures.find(arch => arch.id === state.architectureId);
		if (architecture === undefined) return undefined;
		const rooms = state.minimumBasePriceRoomStates.map(roomState => {
			const roomWithPrice = liveByRoomId.get(roomState.roomId);
			// A room that WebHotelier does not list is an Unpriced Room too: the popup names it from the state.
			const named = roomWithPrice ?? {
				architectureId: state.architectureId, roomMappingType: state.roomMappingType, isAnchor: roomState.isAnchor, price: null,
				room: { id: roomState.roomId, roomName: roomState.roomName, hotelId: 0, numberOfRooms: null, catalogueEligible: true },
			} as PricingRoomWithPriceOutput;
			return { roomState, roomWithPrice: named, livePrice: roomWithPrice?.price ?? null };
		});
		const unpricedMaster = rooms.find(r => r.roomState.isAnchor && r.livePrice === null);
		if (unpricedMaster !== undefined) {
			unpricedRooms.push(unpricedMaster.roomWithPrice);
			continue;
		}
		rooms.filter(r => r.livePrice === null).forEach(r => unpricedRooms.push(r.roomWithPrice));
		const priced = rooms.filter(r => r.livePrice !== null);
		if (priced.length === 0) continue;

		const rateIndex = rates.length;
		rates.push({
			architectureId: state.architectureId,
			recommendationId: state.id,
			roomPrices: priced.map(({ roomState, livePrice }) => ({
				roomId: roomState.roomId,
				currentPrice: roomState.basePrice,
				submittedPrice: round(state.isValid ? roomState.finalPrice : livePrice!),
				isAnchor: roomState.isAnchor,
			})),
		});
		groups.push({
			key: `reco-${state.id}`,
			rateIndex,
			roomMappingType: state.roomMappingType,
			rooms: priced.map(({ roomState, livePrice }, roomIndex) => ({
				roomId: roomState.roomId,
				roomName: roomState.roomName,
				isAnchor: roomState.isAnchor,
				currentPrice: livePrice!,
				derivativeRule: toDerivativeRule(architecture.derivatives.find(der => der.pricingRoom.id === roomState.roomId)?.rule),
				roomIndex,
			})),
		});
	}
	return { mode: 'recommendation', groups, unpricedRooms, defaultValues: { statDate: input.statDate, rates } };
};

// A manual submit: the live rooms (of the tab's room type, or every room), Current = New Rate = the live price.
const createManualPlan = (input: PriceEditPlanInput): PriceEditPlan => {
	const architectureIds = input.architectureIds;
	const tabRooms = architectureIds === undefined ? input.liveRooms
		: input.liveRooms.filter(roomWithPrice => architectureIds.includes(roomWithPrice.architectureId));
	const { shownRooms, unpricedRooms } = splitUnpricedRooms(tabRooms);
	const architectureOrder = [...new Set(shownRooms.map(roomWithPrice => roomWithPrice.architectureId))];
	const grouped = architectureOrder.map(architectureId => shownRooms.filter(roomWithPrice => roomWithPrice.architectureId === architectureId));

	return {
		mode: 'manual',
		unpricedRooms,
		groups: grouped.map((rooms, rateIndex) => ({
			key: `manual-pricing-${rooms[0].architectureId}`,
			rateIndex,
			roomMappingType: rooms[0].roomMappingType,
			rooms: rooms.map((roomWithPrice, roomIndex) => ({
				roomId: roomWithPrice.room.id,
				roomName: roomWithPrice.room.roomName,
				isAnchor: roomWithPrice.isAnchor,
				currentPrice: roomWithPrice.price,
				derivativeRule: toDerivativeRule(roomWithPrice.derivativeRule),
				roomIndex,
			})),
		})),
		defaultValues: {
			statDate: input.statDate,
			rates: grouped.map(rooms => ({
				architectureId: rooms[0].architectureId,
				roomPrices: rooms.map(roomWithPrice => ({
					roomId: roomWithPrice.room.id,
					currentPrice: round(roomWithPrice.price),
					submittedPrice: round(roomWithPrice.price),
					isAnchor: roomWithPrice.isAnchor,
				})),
			})),
		},
	};
};

/**
 * The rows, start values and submit of the Edit Prices popup (ROO2-313 R6). With an open valid recommendation on the
 * tab (and a hotel that may send one) it is a Recommendation Submit; else a manual submit. Current is the live
 * WebHotelier price on every day. Undefined: a state has no architecture (the room configuration has changed).
 */
export const createPriceEditPlan = (input: PriceEditPlanInput): PriceEditPlan | undefined => {
	const openStates = input.states.filter(state => !input.submittedStateIds.includes(state.id));
	const hasOpenValid = openStates.some(state => state.isValid && state.minimumBasePriceRoomStates.length > 0);
	if (input.canSubmitRecommendation && hasOpenValid) return createRecommendationPlan(input, openStates);
	return createManualPlan(input);
};

// The manual submit sends the Current Rate for an empty New Rate, as the Submit Prices modal did.
export const toManualPricingInput = (value: ManualPricingIntegrationInput): ManualPricingIntegrationInput => ({
	...value,
	rates: value.rates.map(rate => ({
		...rate,
		roomPrices: rate.roomPrices.map(roomPrice => ({
			...roomPrice,
			submittedPrice: roomPrice.submittedPrice ?? roomPrice.currentPrice ?? 0,
		})),
	})),
});
